/**
 * Base interface for cross-tab messages.
 *
 * @remarks
 * Provides type-safe foundation for cross-tab communication.
 * Services define their own message type strings and filter accordingly.
 *
 * Message type naming convention: `domain:action`
 * Examples: 'auth:login', 'auth:logout', 'i18n:language_changed'
 */
export interface CrossTabMessage<P = unknown> {
  /** Message type for filtering (convention: 'domain:action') */
  readonly type: string;
  /** Unix timestamp for message ordering/debugging */
  readonly timestamp: number;
  /** Optional payload data */
  readonly payload?: P;
}

/**
 * Type guard to check if unknown value is a valid CrossTabMessage.
 *
 * @remarks
 * Validates the basic structure required for cross-tab communication:
 * - Must be a non-null object
 * - Must have a string `type` property
 * - Must have a number `timestamp` property
 *
 * @param value - Unknown value to check
 * @returns true if value is a valid CrossTabMessage
 */
export const isCrossTabMessage = (value: unknown): value is CrossTabMessage =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as CrossTabMessage).type === 'string' &&
  typeof (value as CrossTabMessage).timestamp === 'number';
